import {Component} from '@angular/core';
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatDialog} from "@angular/material/dialog";
import {RecordingService} from "../../../services/recording.service";
import {SessionService} from "../../../services/session.service";
import {DatePipe} from "@angular/common";
import {PreventDoubleClickDirective} from "../../../lib/prevent-double-click.directive";
import {RecordingInfo, RecordingType, TranscriptionState} from "@allaihealth/util";
import {DictationDialogComponent} from "../dictation-dialog/dictation-dialog.component";
import {RecordingStates} from '../../../lib/enums';
import {BaseRecordingComponent} from "../base-recording.component";
import {Router} from "@angular/router";
import {MatRippleModule} from "@angular/material/core";
import {RecordingToUpload} from "../../../lib/recording-to-upload";
import {Capacitor} from "@capacitor/core";

/** Handles the recording system and the state of the recording page */
@Component({
	selector: 'app-record',
	imports: [
		MatButtonModule,
		MatIconModule,
		DatePipe,
		PreventDoubleClickDirective,
		MatRippleModule
	],
	templateUrl: './record.component.html',
	styleUrl: './record.component.scss'
})
export class RecordComponent extends BaseRecordingComponent {
	readonly RecordingStates = RecordingStates;

	/** Is uploading files enabled? */
	uploadEnabled:boolean;

	/** A previous recording that has already been made */
	public previousRecording:RecordingInfo;

	/** Should the template screen be brought up when done recording? */
	private moveToTemplateWhenDone:boolean = false;

	constructor(private sessionService:SessionService,
	            private matDialog:MatDialog,
	            private router:Router) {
		super();
	}

	override async ngOnInit() {
		super.ngOnInit();

		this.uploadEnabled = this.sessionService.isAdmin && !Capacitor.isNativePlatform();
	}

	override async onNewAppointmentData() {
		this.previousRecording = this.recording;

		await super.onNewAppointmentData();
	}

	/** Reset the recording process to the default (waiting or done) */
	protected override resetToDefaultState() {
		if(this.state != RecordingStates.Unsupported && this.state != RecordingStates.NoDevices) {
			this.state = this.previousRecording ? RecordingStates.Done : RecordingStates.Waiting;
		}
	}

	/** Called when the user uploads a file to the input element on the page */
	async fileInputChange($event:Event) {
		const files = ($event?.target as HTMLInputElement)?.files;

		//set the final blob if a file was uploaded
		if(files instanceof FileList && files.length > 0 && files[0] instanceof Blob) {
			const recording = new RecordingToUpload()
			await recording.save({blob: files[0], duration: 0});
			this.onStop(recording, true);
		} else {
			console.error("Failed to read files from file event");
			this.snackbar.error("Failed to read files");
		}
	}

	/** Open the dictation dialog */
	openDictationDialog() {
		if(this.state == RecordingStates.Recording) {
			this.stopMic();
		}

		this.matDialog.open(DictationDialogComponent, {
			width: "368px",
			disableClose: true,
			data: {
				appointmentId: this.appointment.id
			}
		});
	}

	/** Start recording the user */
	override startMic() {
		this.moveToTemplateWhenDone = false;

		return super.startMic();
	}

	protected override getAudioData() {
		return {
			type: RecordingType.Transcription
		};
	}

	protected override putAudioPartComplete(duration:number) {
		if(this.previousRecording) {
			//add to the duration
			this.previousRecording.audioLength += duration
			this.previousRecording.modified = new Date().getTime();
		} else {
			//make a stub recording while we finish uploading
			this.previousRecording = {
				appointmentId: this.appointment.id,
				audioLength: duration,
				state: TranscriptionState.Recording,
				parts: 1,
				created: new Date().getTime(),
				modified: new Date().getTime(),
				organizationId: this.appointment.id_organization,
				patientId: this.appointment.id_patient,
				userId: this.appointment.id_provider,
				jobName: ""
			}
		}

		this.appointmentService.addNewRecording(this.previousRecording);

		if(this.moveToTemplateWhenDone) {
			this.moveToTemplateWhenDone = false;
			this.moveToTemplatePage();
		}
	}

	/** Called when the user wants to go to the template page either while recording or after */
	selectTemplate() {
		if(this.state == RecordingStates.Recording) {
			this.moveToTemplateWhenDone = true;
			this.stopMic();
		} else {
			this.moveToTemplatePage();
		}
	}

	private moveToTemplatePage() {
		this.router.navigate(["/appointments", this.previousRecording.appointmentId, "template"]).then();
	}

	override get previousRecordingTime() {
		return (this.previousRecording?.audioLength ?? 0) * 1000;
	}

	/** Is the recording in a state where the appointment nav shouldn't be shown? */
	get isBlockingRecordingState() {
		return this.state == RecordingStates.Recording;
	}

	/** A list of file extensions accepted for file uploads */
	get mimesAccepted() {
		return RecordingService.mimesAccepted;
	}
}

